import PageLoading from 'components/common/loading/PageLoading'
import { useAuth } from 'pages/authenticated/AuthContext'
import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { getBackgroundPathFromState } from '../utils/authNavigation'

interface GuestOnlyRouteProps {
  children: ReactNode
}

const AUTH_PATH_PREFIXES = ['/auth', '/create-user', '/reset-password']

function isAuthPath(path: string): boolean {
  const pathname = path.split('?')[0]?.split('#')[0] || ''
  return AUTH_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

/**
 * Blocks already-authenticated users from guest screens (login / register).
 * Auth status is evaluated once on mount so in-page login/register can still
 * navigate to their intended post-auth destination without racing this guard.
 */
const GuestOnlyRoute = ({ children }: GuestOnlyRouteProps) => {
  const { validateToken } = useAuth()
  const location = useLocation()
  const [status, setStatus] = useState<'checking' | 'guest' | 'authed'>(
    'checking'
  )

  useEffect(() => {
    let mounted = true
    const run = async () => {
      const ok = await validateToken()
      if (!mounted) return
      setStatus(ok ? 'authed' : 'guest')
    }
    void run()
    return () => {
      mounted = false
    }
  }, [validateToken])

  if (status === 'checking') {
    return <PageLoading />
  }

  if (status === 'authed') {
    const fromPath = getBackgroundPathFromState(location.state, '')
    const dest =
      fromPath && !isAuthPath(fromPath) ? fromPath : '/account'
    return <Navigate to={dest} replace />
  }

  return <>{children}</>
}

export default GuestOnlyRoute
